<template>
    <div :class="classes" class="flex flex-col">
        <label v-if="label" class="block text-sm font-medium text-gray-700">{{ label }}</label>
        <div :class="classes" class="relative">
            <!-- Multiselect without tagging support -->
            <MultiSelect
                v-model="localModelValue"
                :options="options"
                :multiple="true"
                :taggable="false"
                :track-by="trackBy"
                :label="optionLabel"
                :placeholder="isEmpty ? 'No options available' : placeholder"
                :disabled="localDisabled"
                :searchable="false"
                :hide-selected="false"
                :show-labels="true"
                :close-on-select="false"
                class="ggms-multiselect"
                @input="onInput"
                :open-direction="openDirection"
                :max-height="maxHeight"
            >
                <template #caret>
                    <div class="absolute justify-end flex w-full mt-3 pr-4">
                        <svg
                            width="14"
                            height="14"
                            viewBox="0 0 14 14"
                            xmlns="http://www.w3.org/2000/svg"
                            class="p-icon p-dropdown-trigger-icon"
                            aria-hidden="true"
                            data-pc-section="dropdownicon"
                        >
                            <path
                                d="M7.01744 10.398C6.91269 10.3985 6.8089 10.378 6.71215 10.3379C6.61541 10.2977 6.52766 10.2386 6.45405 10.1641L1.13907 4.84913C1.03306 4.69404 0.985221 4.5065 1.00399 4.31958C1.02276 4.13266 1.10693 3.95838 1.24166 3.82747C1.37639 3.69655 1.55301 3.61742 1.74039 3.60402C1.92777 3.59062 2.11386 3.64382 2.26584 3.75424L7.01744 8.47394L11.769 3.75424C11.9189 3.65709 12.097 3.61306 12.2748 3.62921C12.4527 3.64535 12.6199 3.72073 12.7498 3.84328C12.8797 3.96582 12.9647 4.12842 12.9912 4.30502C13.0177 4.48162 12.9841 4.662 12.8958 4.81724L7.58083 10.1322C7.50996 10.2125 7.42344 10.2775 7.32656 10.3232C7.22968 10.3689 7.12449 10.3944 7.01744 10.398Z"
                                fill="#71717A"
                            ></path>
                        </svg>
                    </div>
                </template>
                <!-- beforeList slot: Select All / Deselect All control, only visible when dropdown is open -->
                <template #beforeList>
                    <div class="flex items-center gap-2 py-2 px-3 cursor-pointer select-none" @click="toggleAll">
                        <!-- Static checkbox icon for Select All/Deselect All, not interactive -->
                        <span>
                            <svg
                                v-if="isAllSelected"
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <rect x="2" y="2" width="16" height="16" rx="4" fill="#0090BE" />
                                <path
                                    d="M6 10.5L9 13.5L14 8.5"
                                    stroke="white"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                            <svg
                                v-else
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <rect
                                    x="2"
                                    y="2"
                                    width="16"
                                    height="16"
                                    rx="4"
                                    fill="#fff"
                                    stroke="#d1d5db"
                                    stroke-width="2"
                                />
                            </svg>
                        </span>
                        <span class="text-sm">{{ isAllSelected ? "Deselect All" : "Select All" }}</span>
                    </div>
                </template>
                <!-- Tag display for selected values: chip or plain text based on display prop -->
                <template #tag="{ option, remove }">
                    <!-- Show chip if display is 'chip', otherwise show plain text -->
                    <template v-if="isChipDisplay">
                        <GgmsChip
                            :bgColor="getBgColor(option)"
                            :textColor="getTextColor(option)"
                            class="mr-2"
                            @onRemove="remove(option)"
                        >
                            {{ resolveLabel(option, optionLabel) }}
                        </GgmsChip>
                    </template>
                    <template v-else>
                        <span class="mr-2">{{ resolveLabel(option, optionLabel) }}</span>
                    </template>
                </template>
                <!-- Option slot: show a non-interactive checkbox next to chip or plain text -->
                <template #option="{ option }">
                    <div class="ggms-multiselect-option-row flex items-center gap-2">
                        <!-- Static checkbox icon for option selection, not interactive -->
                        <span>
                            <svg
                                v-if="isOptionSelected(option)"
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <rect x="2" y="2" width="16" height="16" rx="4" fill="#0090BE" />
                                <path
                                    d="M6 10.5L9 13.5L14 8.5"
                                    stroke="white"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                            <svg
                                v-else
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <rect
                                    x="2"
                                    y="2"
                                    width="16"
                                    height="16"
                                    rx="4"
                                    fill="#fff"
                                    stroke="#d1d5db"
                                    stroke-width="2"
                                />
                            </svg>
                        </span>
                        <template v-if="isChipDisplay">
                            <GgmsChip
                                :bgColor="getBgColor(option)"
                                :textColor="getTextColor(option)"
                                :isRemovable="false"
                            >
                                {{ resolveLabel(option, optionLabel) }}
                            </GgmsChip>
                        </template>
                        <template v-else>
                            <span>{{ resolveLabel(option, optionLabel) }}</span>
                        </template>
                    </div>
                </template>
            </MultiSelect>
            <p v-if="localErrorMessage" class="mt-2 text-sm text-red-600">{{ localErrorMessage }}</p>
        </div>
    </div>
</template>

<script lang="ts" setup>
import "vue-multiselect/dist/vue-multiselect.css"
import { defineProps, defineEmits, computed, ref, watch, onMounted } from "vue"
import MultiSelect from "vue-multiselect"
import GgmsChip from "@/components/GgmsChip.vue"
import { useTagStore } from "@/stores/tag"
import { useOriginStore } from "@/stores/origin"

interface Props {
    modelValue: any[]
    options: any[]
    display?: "comma" | "chip"
    label?: string
    optionLabel?: string
    optionValue?: string
    placeholder?: string
    isShowClear?: boolean
    classes?: string
    errorMessage?: string
    invalid?: boolean
    disabled?: boolean
    emptyMessage?: string
    maxSelectedLabels?: number
    openDirection?: "above" | "below"
    maxHeight?: number
}

const props = withDefaults(defineProps<Props>(), {
    placeholder: "Select Element",
    display: "comma",
    optionLabel: "displayName",
    optionValue: "_id",
    maxSelectedLabels: 3,
})

const emit = defineEmits(["update:modelValue", "change"])

const localModelValue = ref<any[]>(props.modelValue || [])

watch(
    () => props.modelValue,
    (newValue) => {
        localModelValue.value = newValue || []
    }
)

watch(
    localModelValue,
    (newValue) => {
        emit("update:modelValue", newValue)
        emit("change", newValue)
    },
    { deep: true }
)

const localErrorMessage = ref(props.errorMessage)
const localInvalid = ref(props.invalid)

const errorStyle = computed(() => (localInvalid.value ? "error-style" : ""))

const isChipDisplay = computed(() => props.display === "chip")

const localDisabled = computed(() => props.disabled || !props.options?.length)

const tagStore = useTagStore()
const originStore = useOriginStore()
const cachedTags = computed(() => tagStore.cachedTags)
const cachedOrigins = computed(() => originStore.cachedOrigins)

const trackBy = computed(() => props.optionValue || "_id")
const optionLabel = computed(() => props.optionLabel || "displayName")

function getBgColor(option: any): string {
    return cachedTags.value[option._id]?.backgroundColor || cachedOrigins.value[option._id]?.backgroundColor
}

function getTextColor(option: any): string {
    return cachedTags.value[option._id]?.foregroundColor || cachedOrigins.value[option._id]?.foregroundColor
}

function onInput(value: any[]) {
    localModelValue.value = value
}

watch(
    () => props.errorMessage,
    (value) => {
        localErrorMessage.value = value
    }
)

watch(
    () => props.invalid,
    (value) => {
        localInvalid.value = value
    }
)

const isEmpty = computed(() => !props.options.length)

onMounted(() => {
    localInvalid.value = false
    localErrorMessage.value = ""
})

// Helper to check if an option is selected
function isOptionSelected(option: any): boolean {
    return localModelValue.value.some((selected: any) => selected[trackBy.value] === option[trackBy.value])
}

// Helper to check if all options are selected
const isAllSelected = computed(() => {
    if (!props.options?.length) return false
    return (
        localModelValue.value.length === props.options.length &&
        props.options.every((opt) => localModelValue.value.some((sel) => sel[trackBy.value] === opt[trackBy.value]))
    )
})

// Toggle all selection
function toggleAll() {
    if (isAllSelected.value) {
        localModelValue.value = []
    } else {
        localModelValue.value = [...props.options]
    }
}

// Utility function to resolve nested property by path (e.g., 'smth.label')
/**
 * Resolves a nested property from an object using a dot-separated path string.
 * @param obj The object to resolve from.
 * @param path The dot-separated path string (e.g., 'smth.label').
 * @returns The resolved value or undefined if not found.
 */
function resolveLabel(obj: any, path: string): any {
    if (!obj || !path) return undefined
    return path.split(".").reduce((acc, part) => (acc && acc[part] !== undefined ? acc[part] : undefined), obj)
}
</script>

<style scoped>
:deep(.multiselect) {
    all: initial;
    font-family: inherit !important;
    font-size: inherit !important;
}

/* Remove background color on hover and selection for vue-multiselect options (correct class names) */
:deep(.multiselect__option),
:deep(.multiselect__option--highlight),
:deep(.multiselect__option--selected),
:deep(.multiselect__option--selected.multiselect__option--highlight) {
    background: none !important;
    color: inherit !important;
}

/* Hide the help text (e.g. 'Press enter to remove', 'Selected') in the options dropdown */
:deep(.multiselect__option--highlight::after),
:deep(.multiselect__option--selected::after),
:deep(.multiselect__option--selected.multiselect__option--highlight::after) {
    display: none !important;
    content: none !important;
}

/* Custom hover effect for option row */
.ggms-multiselect-option-row {
    transition: background 0.2s;
    padding-top: 10px;
    padding-bottom: 10px;
    padding-left: 12px;
    /* border-radius: 4px; */
}
.ggms-multiselect-option-row:hover {
    background: #f5f5f5;
}

/* Remove margins and paddings from the options container */
:deep(.multiselect__content),
:deep(.multiselect__content-wrapper) {
    margin: 0 !important;
    padding: 0 !important;
}
:deep(.multiselect__option) {
    padding-top: 0px;
    padding-bottom: 0px;
    padding-left: 0;
    padding-right: 0;
}

:deep(.multiselect__content-wrapper) {
    /* Add a subtle shadow to the dropdown options for better separation */
    width: 100%;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
    margin: 0 !important;
    padding: 0 !important;
}

:deep(.multiselect__tags) {
    border-color: rgb(209 213 219) !important;
    border-width: 1px !important;
}

:deep(.multiselect--active .multiselect__tags) {
    /* Blue border when the dropdown is open/active */
    border-color: var(--primary-color-500) !important;
    border-width: 2px !important;
}

:deep(.ggms-multiselect-caret) {
    width: 10px;
    height: 10px;
    background-color: var(--primary-color-500);
}

:deep(.multiselect__input::placeholder),
:deep(.multiselect__placeholder) {
    /* Custom placeholder color for multiselect input and placeholder */
    color: #71717a !important; /* Tailwind slate-400 */
}
</style>
